import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Button,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    alpha,
} from '@mui/material';

const PLANS = ['starter', 'pro', 'enterprise'];
const CYCLES = ['monthly', 'annual'];
const STATUSES = ['active', 'trial', 'cancelled', 'inactive', 'payment_required'];

const PLAN_DEFAULTS = {
    starter: { max_users: 1, allowed_languages: 5 },
    pro: { max_users: 5, allowed_languages: 15 },
    enterprise: { max_users: 999, allowed_languages: 999 },
};

const EditSubscriptionDialog = ({
    open,
    onClose,
    subscription,
    organizationId,
    onCreateSubscription,
    onUpdateSubscription,
    colors,
    t,
}) => {
    const isEdit = !!subscription;

    const [plan, setPlan] = useState('starter');
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [status, setStatus] = useState('active');
    const [maxUsers, setMaxUsers] = useState(1);
    const [allowedLanguages, setAllowedLanguages] = useState(5);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (subscription) {
            setPlan(subscription.plan || 'starter');
            setBillingCycle(subscription.type || 'monthly');
            setStatus(subscription.status || 'active');
            setMaxUsers(subscription.max_users || 1);
            setAllowedLanguages(subscription.allowed_languages || 5);
        } else {
            setPlan('starter');
            setBillingCycle('monthly');
            setStatus('active');
            setMaxUsers(1);
            setAllowedLanguages(5);
        }
    }, [subscription, open]);

    const handlePlanChange = (newPlan) => {
        setPlan(newPlan);
        const defaults = PLAN_DEFAULTS[newPlan];
        if (defaults) {
            setMaxUsers(defaults.max_users);
            setAllowedLanguages(defaults.allowed_languages);
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            if (isEdit) {
                await onUpdateSubscription(organizationId, {
                    plan,
                    type: billingCycle,
                    status,
                    max_users: maxUsers,
                    allowed_languages: allowedLanguages,
                });
            } else {
                await onCreateSubscription(organizationId, {
                    plan,
                    billing_cycle: billingCycle,
                    status,
                    max_users: maxUsers,
                    allowed_languages: allowedLanguages,
                });
            }
            onClose();
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    border: `1px solid ${colors.border}`,
                    boxShadow: colors.isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.1)',
                },
            }}
        >
            <DialogTitle sx={{ pb: 1, pt: 3, px: 3, fontWeight: 600, fontSize: '1.25rem', color: colors.text }}>
                {isEdit ? t('edit_subscription', 'Edit Subscription') : t('create_subscription', 'Create Subscription')}
            </DialogTitle>
            <DialogContent sx={{ p: 3, pt: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>{t('plan', 'Plan')}</InputLabel>
                        <Select value={plan} label={t('plan', 'Plan')} onChange={(e) => handlePlanChange(e.target.value)} sx={{ borderRadius: '8px' }}>
                            {PLANS.map((p) => (
                                <MenuItem key={p} value={p} sx={{ textTransform: 'capitalize' }}>{p}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                        <InputLabel>{t('billing_cycle', 'Billing Cycle')}</InputLabel>
                        <Select value={billingCycle} label={t('billing_cycle', 'Billing Cycle')} onChange={(e) => setBillingCycle(e.target.value)} sx={{ borderRadius: '8px' }}>
                            {CYCLES.map((c) => (
                                <MenuItem key={c} value={c} sx={{ textTransform: 'capitalize' }}>{c}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                        <InputLabel>{t('status.label', 'Status')}</InputLabel>
                        <Select value={status} label={t('status.label', 'Status')} onChange={(e) => setStatus(e.target.value)} sx={{ borderRadius: '8px' }}>
                            {STATUSES.map((s) => (
                                <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label={t('max_users', 'Max Users')}
                            type="number"
                            value={maxUsers}
                            onChange={(e) => setMaxUsers(parseInt(e.target.value, 10) || 1)}
                            size="small"
                            fullWidth
                            inputProps={{ min: 1 }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                        />
                        <TextField
                            label={t('allowed_languages', 'Allowed Languages')}
                            type="number"
                            value={allowedLanguages}
                            onChange={(e) => setAllowedLanguages(parseInt(e.target.value, 10) || 1)}
                            size="small"
                            fullWidth
                            inputProps={{ min: 1 }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
                <Button onClick={onClose} sx={{ color: colors.textSecondary, textTransform: 'none', fontWeight: 500 }}>
                    {t('cancel', 'Cancel')}
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disableElevation
                    disabled={loading}
                    sx={{
                        bgcolor: colors.primary, color: 'white', textTransform: 'none',
                        fontWeight: 600, px: 3, py: 1, borderRadius: '8px',
                        '&:hover': { bgcolor: colors.primaryDark },
                        '&.Mui-disabled': { bgcolor: alpha(colors.primary, 0.6), color: 'white' },
                    }}
                >
                    {loading ? t('saving', 'Saving...') : isEdit ? t('update', 'Update') : t('create', 'Create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditSubscriptionDialog;
